import React from 'react';
import { IconBaseProps } from 'react-icons/lib';

// Import all icons from react-icons individually to avoid conflicts
import {
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineHome,
  AiOutlineProject,
  AiOutlineSave,
  AiOutlineWarning,
  AiFillWarning,
  AiFillExclamationCircle,
} from 'react-icons/ai';

import {
  // Bootstrap Icons
  BiDownload,
} from 'react-icons/bi';

import {
  // Bootstrap Icons (Bs)
  BsThreeDotsVertical,
} from 'react-icons/bs';

import {
  // Feather Icons
  FiExternalLink,
  FiLink,
  FiMaximize2,
  FiMinimize2,
  FiSettings,
} from 'react-icons/fi';

import {
  // Font Awesome Icons
  FaArrowDown,
  FaArrowUp,
  FaCaretDown,
  FaCaretUp,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaExclamation,
  FaExclamationCircle,
  FaMinus,
  FaPencilAlt,
  FaPlus,
  FaProjectDiagram,
  FaRegHandPointer,
  FaTrash,
  FaTrashAlt,
} from 'react-icons/fa';





import {
  // Heroicons
  HiCheckCircle,
  HiInformationCircle,
  HiQuestionMarkCircle,
} from 'react-icons/hi';

import {
  IoMdClose,
  IoMdMenu,
} from 'react-icons/io';

import {
  // Material Design Icons
  MdChatBubble,
  MdChatBubbleOutline,
  MdOpenInFull,
  MdOutlineCancel,
  MdOutlineEmail,
} from 'react-icons/md';

import {
  // Simple Line Icons
  SlLogout,
} from 'react-icons/sl';

import {
  GrProjects,
} from 'react-icons/gr';

export type IconName = 
  // Action Icons
  | 'check' | 'check-circle' | 'delete' | 'edit' | 'save' | 'download' | 'upload' | 'close' | 'cancel'
  | 'plus' | 'minus' | 'trash' | 'trash-alt' | 'pencil' | 'settings' | 'logout' | 'menu'
  
  // Navigation Icons
  | 'chevron-down' | 'chevron-up' | 'chevron-right' | 'arrow-down' | 'arrow-up' | 'caret-down' | 'caret-up'
  | 'external-link' | 'link' | 'home' | 'project' | 'projects'
  
  // Status Icons
  | 'warning' | 'warning-filled' | 'exclamation' | 'exclamation-circle' | 'information' | 'question'
  | 'success' | 'error' | 'danger'
  
  // UI Icons
  | 'three-dots' | 'maximize' | 'minimize' | 'email' | 'chat' | 'chat-outline' | 'hand-pointer'
  | 'project-diagram' | 'open-full';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export type IconColor = 
  | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'light' | 'dark'
  | 'white' | 'black' | 'gray' | 'red' | 'green' | 'blue' | 'yellow' | 'orange'
  | 'inherit' | 'current';

export interface IconProps extends Omit<IconBaseProps, 'size' | 'color'> {
  name: IconName;
  size?: IconSize | number;
  color?: IconColor | string;
  className?: string;
}

const sizeMap: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
  '2xl': 48,
};

const colorMap: Record<IconColor, string> = {
  primary: '#007bff',
  secondary: '#6c757d',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40',
  white: '#ffffff',
  black: '#000000',
  gray: '#6c757d',
  red: '#dc3545',
  green: '#28a745',
  blue: '#007bff',
  yellow: '#ffc107',
  orange: '#fd7e14',
  inherit: 'inherit',
  current: 'currentColor',
};

const iconMap: Record<IconName, React.ComponentType<IconBaseProps>> = {
  // Action Icons
  'check': FaCheck,
  'check-circle': HiCheckCircle,
  'delete': AiOutlineDelete,
  'edit': AiOutlineEdit,
  'save': AiOutlineSave,
  'download': BiDownload,
  'upload': BiDownload, // Using download as upload for now
  'close': IoMdClose,
  'cancel': MdOutlineCancel,
  'plus': FaPlus,
  'minus': FaMinus,
  'trash': FaTrash,
  'trash-alt': FaTrashAlt,
  'pencil': FaPencilAlt,
  'settings': FiSettings,
  'logout': SlLogout,
  'menu': IoMdMenu,
  
  // Navigation Icons
  'chevron-down': FaChevronDown,
  'chevron-up': FaChevronUp,
  'chevron-right': FaChevronRight,
  'arrow-down': FaArrowDown,
  'arrow-up': FaArrowUp,
  'caret-down': FaCaretDown,
  'caret-up': FaCaretUp,
  'external-link': FiExternalLink,
  'link': FiLink,
  'home': AiOutlineHome,
  'project': AiOutlineProject,
  'projects': GrProjects,
  
  // Status Icons
  'warning': AiOutlineWarning,
  'warning-filled': AiFillWarning,
  'exclamation': FaExclamation,
  'exclamation-circle': FaExclamationCircle,
  'information': HiInformationCircle,
  'question': HiQuestionMarkCircle,
  'success': HiCheckCircle,
  'error': AiFillExclamationCircle,
  'danger': AiFillExclamationCircle,
  
  // UI Icons
  'three-dots': BsThreeDotsVertical,
  'maximize': FiMaximize2,
  'minimize': FiMinimize2,
  'email': MdOutlineEmail,
  'chat': MdChatBubble,
  'chat-outline': MdChatBubbleOutline,
  'hand-pointer': FaRegHandPointer,
  'project-diagram': FaProjectDiagram,
  'open-full': MdOpenInFull,
};

export const Icon: React.FC<IconProps> = ({ 
  name, 
  size = 'md', 
  color = 'current', 
  className,
  ...props 
}) => {
  const IconComponent = iconMap[name];
  
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in icon library`);
    return null;
  }

  const iconSize = typeof size === 'number' ? size : sizeMap[size];
  const iconColor = colorMap[color as IconColor] || color;

  return (
    <IconComponent
      size={iconSize}
      color={iconColor}
      className={className}
      {...props}
    />
  );
};

// Convenience functions for common icon patterns
export const createIcon = (name: IconName) => {
  return (props: Omit<IconProps, 'name'>) => <Icon name={name} {...props} />;
};

// Pre-defined icon components for common use cases
export const CheckIcon = createIcon('check');
export const CheckCircleIcon = createIcon('check-circle');
export const DeleteIcon = createIcon('delete');
export const EditIcon = createIcon('edit');
export const SaveIcon = createIcon('save');
export const DownloadIcon = createIcon('download');
export const CloseIcon = createIcon('close');
export const CancelIcon = createIcon('cancel');
export const PlusIcon = createIcon('plus');
export const MinusIcon = createIcon('minus');
export const TrashIcon = createIcon('trash');
export const PencilIcon = createIcon('pencil');
export const SettingsIcon = createIcon('settings');
export const LogoutIcon = createIcon('logout');
export const MenuIcon = createIcon('menu');

export const ChevronDownIcon = createIcon('chevron-down');
export const ChevronUpIcon = createIcon('chevron-up');
export const ChevronRightIcon = createIcon('chevron-right');
export const ArrowDownIcon = createIcon('arrow-down');
export const ArrowUpIcon = createIcon('arrow-up');
export const ExternalLinkIcon = createIcon('external-link');
export const LinkIcon = createIcon('link');
export const HomeIcon = createIcon('home');
export const ProjectIcon = createIcon('project');

export const WarningIcon = createIcon('warning');
export const WarningFilledIcon = createIcon('warning-filled');
export const ExclamationIcon = createIcon('exclamation');
export const ExclamationCircleIcon = createIcon('exclamation-circle');
export const InformationIcon = createIcon('information');
export const QuestionIcon = createIcon('question');
export const SuccessIcon = createIcon('success');
export const ErrorIcon = createIcon('error');
export const DangerIcon = createIcon('danger');

export const ThreeDotsIcon = createIcon('three-dots');
export const MaximizeIcon = createIcon('maximize');
export const MinimizeIcon = createIcon('minimize');
export const EmailIcon = createIcon('email');
export const ChatIcon = createIcon('chat');
export const ChatOutlineIcon = createIcon('chat-outline');
export const HandPointerIcon = createIcon('hand-pointer');
export const ProjectDiagramIcon = createIcon('project-diagram');
export const OpenFullIcon = createIcon('open-full');

export default Icon; 